import * as React from "react";
import { Button as FluentButton, Radio, RadioGroup } from "@fluentui/react-components";
import type { RadioGroupProps } from "@fluentui/react-components";
import { Divider, Link, mergeClasses } from "@microsoft/shifts-ui-core";
import { NewShiftsDialog } from "../../common/NewShiftsDialog/NewShiftsDialog";
import { PeopleTeamRegular, CalendarLtrRegular, OpenRegular } from "@fluentui/react-icons";
import * as moment from "moment";
import DateTimeFormatter, { DateTimeFormatType } from "sh-application/utility/DateTimeFormatter";
import type { useScheduleEditorStyles } from "./ScheduleGenerationEditorPage.style";
import { GenerateOptions } from "../smartScheduleUtils";

const styles = require("./ScheduleGenerationEditorPage.scss");

export interface LocalizedStrings {
    get: (key: string, ...args: unknown[]) => string;
}

export interface ExistingShiftsDialogProps {
    isOpen: boolean;
    selectedGroupName: string;
    startDate: moment.Moment;
    endDate: moment.Moment;
    existingShiftsCount: number;
    generateOption: string;
    onGenerateOptionChange: NonNullable<RadioGroupProps["onChange"]>;
    onDismiss: () => void;
    onGenerate: () => void;
    strings: LocalizedStrings;
    commonStrings: LocalizedStrings;
    scheduleStrings: LocalizedStrings;
    pageStyles: ReturnType<typeof useScheduleEditorStyles>;
    currentLocale: string;
}

const formatDate = (date: Date): string => {
    return date ? DateTimeFormatter.getDateTimeAsString(date, DateTimeFormatType.Date_DayShortDateMonthYear) : ""; // e.g., "Wed, Dec 25, 2024"
};

export const ExistingShiftsDialog: React.FC<ExistingShiftsDialogProps> = ({
    isOpen,
    selectedGroupName,
    startDate,
    endDate,
    existingShiftsCount,
    generateOption,
    onGenerateOptionChange,
    onDismiss,
    onGenerate,
    strings,
    commonStrings,
    scheduleStrings,
    pageStyles,
    currentLocale
}) => {
    const dialogTitleText = strings.get("handleExistingShiftsPopupTitle");
    const dateRangeLabel = `${formatDate(startDate.toDate())} ${strings.get("dateRangeToText")} ${formatDate(endDate.toDate())}`;
    const groupLabelText = scheduleStrings.get("newGroupLabel");

    const renderRadioLabel = (title: string, description: string) => (
        <div className={styles.choiceOptionWrapper}>
            <div className={styles.choiceOptionTitle}>{title}</div>
            <div className={styles.choiceOptionDescription}>{description}</div>
        </div>
    );

    const localizedLink = `https://support.microsoft.com/${currentLocale}/office/export-a-shifts-schedule-to-excel-8e604434-de77-4aae-8e87-561eaab902cf`;
    const existingShiftsLabel = existingShiftsCount > 1 ? strings.get("existingShiftsPluralLabel") : strings.get("existingShiftSingularLabel");

    const content = (
        <>
            <Divider className={styles.generateDialogDivider} />
            <div className={styles.existingShiftsDescription}>
                <span className={styles.existingShiftsCount}>{existingShiftsCount} {existingShiftsLabel}</span> {strings.get("existingShiftsFoundDescription")}
            </div>
            <div className={styles.generateOptionsContainer}>
                <RadioGroup
                    value={generateOption}
                    onChange={onGenerateOptionChange}
                    name="generateScheduleOption"
                >
                    <Radio
                        value={GenerateOptions.CONSIDER}
                        className={generateOption === GenerateOptions.CONSIDER ? styles.checked : undefined}
                        label={renderRadioLabel(
                            strings.get("considerExistingShiftsOption"),
                            strings.get("considerExistingShiftsDescription")
                        )}
                    />
                    <Radio
                        value={GenerateOptions.REPLACE}
                        className={generateOption === GenerateOptions.REPLACE ? styles.checked : undefined}
                        label={renderRadioLabel(
                            strings.get("replaceExistingScheduleOption"),
                            strings.get("replaceExistingScheduleDescription").replace("{0}", existingShiftsCount.toString())
                        )}
                    />
                </RadioGroup>
            </div>

            <div className={styles.replaceWarningText}>
                {strings.get("replaceCannotBeUndoneWarning")} {" "}
                <span className={mergeClasses(styles.replaceWarningLink, pageStyles.link)}>
                    <Link href={localizedLink} target="_blank" rel="noopener noreferrer">
                        {strings.get("exportingImportingSchedules")}
                        <OpenRegular className={pageStyles.externalLinkIcon} />
                    </Link>
                </span>
            </div>
        </>
    );

    const actions = (
        <div className={styles.generateDialogActions}>
            <FluentButton appearance="secondary" onClick={onDismiss}>{commonStrings.get("cancel")}</FluentButton>
            <FluentButton appearance="primary" onClick={onGenerate}>{strings.get("generateScheduleButton")}</FluentButton>
        </div>
    );

    const title = (
        <div className={styles.generateDialogTitle}>
            <span>{dialogTitleText}</span>
            <div className={mergeClasses(styles.generateDialogTitleMeta, styles.existingShiftsDialogTitleMeta)}>
                <div className={styles.generateDialogMetaItem}>
                    <PeopleTeamRegular aria-hidden="true" className={styles.generateDialogMetaIcon} />
                    <span className={styles.generateDialogMetaLabel}>{groupLabelText}:</span>
                    <span className={styles.generateDialogMetaValue}>{selectedGroupName}</span>
                </div>
                <div className={styles.generateDialogVerticalDivider}></div>
                <div className={styles.generateDialogMetaItem}>
                    <CalendarLtrRegular aria-hidden="true" className={styles.generateDialogMetaIcon} />
                    <span className={styles.generateDialogMetaValue}>{dateRangeLabel}</span>
                </div>
            </div>
        </div>
    );

    return (
        <NewShiftsDialog
            title={title}
            isOpen={isOpen}
            onDismiss={onDismiss}
            content={content}
            actions={actions}
            customStyles={{ dialogBodyContent: pageStyles.editorDialogBodyContent, dialogTitle: styles.generateDialogTitleContainer }}
            showDismiss={true}
        />
    );
};
